import React, { useState, useRef } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { ResponsiveLine } from '@nivo/line';
import { Fade, Collapse } from '@mui/material';

// Generate normal distribution data points with offset
const generateNormalDistribution = (mean = 50, stdDev = 15, points = 20, offset = 0) => {
  const data = [];
  const min = 0;
  const max = 100;
  const step = (max - min) / points;

  // Generate the raw normal distribution data
  for (let i = 0; i <= points; i++) {
    const x = min + step * i;
    const exponent = -Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2));
    const y = (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent);
    data.push({ x: Math.round(x), y });
  }

  // Find the minimum y value
  const minY = Math.min(...data.map(d => d.y));

  // Shift the whole curve so its minimum starts from 0 (plus any offset)
  const adjusted = data.map(d => ({
    x: d.x,
    y: (d.y - minY) * 100 + offset
  }));

  return adjusted;
};

// Define traits with their distributions and insights
const traitDefinitions = {
  ambition: {
    id: 'Ambition',
    color: '#ffa268ff',
    data: generateNormalDistribution(53, 14, 20, 0),
    insight:
      "Do you need someone chasing aspirational goals, or just someone who has a steady job and is free weekends?",
    altInsight:
      "Do I feel expected to chase aspirational goals, or is just keeping a steady job and free weekends enough?"
  },
  attractiveness: {
    id: 'Attractiveness',
    color: '#ff7ea9ff',
    data: generateNormalDistribution(50, 13, 20, 0),
    insight:
      "Do you need someone who turns heads, or just someone you find cute once you get to know them?",
    altInsight:
      "Do I feel expected to turn heads, or do people seem to care more about what's inside?"
  },
  career: {
    id: 'Career',
    color: '#e65e5eff',
    data: generateNormalDistribution(49, 15, 20, 0),
    insight:
      "Do you need high six figures, or just someone who pays their bills on time and doesn't stress about rent?",
    altInsight:
      "Do I feel expected to have an impressive career and earn a lot, or is that not a pressure I feel when dating?"
  },
  communication: {
    id: 'Communication',
    color: '#6bd1caff',
    data: generateNormalDistribution(51, 14, 20, 0.),
    insight:
      "Do they need to articulate feelings extremely well, or can you accept needing to be more patient during arguments?",
    altInsight:
      "Do I feel expected to articulate feelings extremely well, or is being a work in progress good enough?"
  },
  confidence: {
    id: 'Confidence',
    color: '#ffb294ff',
    data: generateNormalDistribution(51, 14, 20, 0),
    insight:
      "Looking for someone who’s confident on every front: career, love, life, or is some self-doubt and uncertainty okay?",
    altInsight:
      "Do I feel expected to move with great confidence in all areas of life, or is consistent effort good enough?"
  },
  education: {
    id: 'Education',
    color: '#5B8DEE',
    data: generateNormalDistribution(50, 13, 20, 0),
    insight:
      "Do you need an advanced degree, or just someone who reads a bit and tries to stay curious?",
    altInsight:
      "Do I feel others care a great amount about academic background in a partner, or not so much?"
  },
  height: {
    id: 'Height',
    color: '#75dec2ff',
    data: generateNormalDistribution(50, 13.5, 20, 0),
    insight:
      "Do you need 6 feet tall, or do you think you could actually get over it as long as they're taller?",
    altInsight:
      "Do I feel height plays a huge part in attracting a partner, or is being taller than the other person good enough?"
  },
  humor: {
    id: 'Humor',
    color: '#ff7e9cff',
    data: generateNormalDistribution(52, 15, 20, 0),
    insight:
      "Do you need someone who keeps you laughing on the first date, or is someone who smiles politely at your jokes good enough?",
    altInsight:
      "Do I feel expected to keep people laughing for it to be a great first date, or not so much?"
  },
  intelligence: {
    id: 'Intelligence',
    color: '#c596ffff',
    data: generateNormalDistribution(49, 14, 20, 0),
    insight:
      "Do you need someone brilliant in various domains, or is just smart enough to have interesting conversations fine?",
    altInsight:
      "Do I feel expected to be highly intelligent in various domains, or just smart enough to have good conversations?"
  },
  kindness: {
    id: 'Kindness',
    color: '#8ee1ffff',
    data: generateNormalDistribution(47, 13, 28, 0),
    insight:
      "Do you need someone deeply kind, or is someone who means well most of the time alright?",
    altInsight:
      "Do I feel the level of kindness expected from others is unrealistic, or is being generally well-meaning enough?"
  }
};


const initialSentiments = [
  {
    id: 'lackOfMatches',
    title: "There's not enough high-quality or suitable options",
    subtitle: "...and the good ones out there aren't committal or only into casual relationships."
  },
  {
    id: 'difficultyBeingCompetitive',
    title: "It's hard to be competitive in this dating market",
    subtitle: "It feels like you need to be exceptionally attractive and successful just to get a chance."
  }
];

export default function TraitDistributionChart() {
  const [selectedSentiment, setSelectedSentiment] = useState(null);
  const [lastSelectedTrait, setLastSelectedTrait] = useState(null);
const [activeTraits, setActiveTraits] = useState({
    ambition: false,
  attractiveness: false,
    career: false,
  communication: false,
  confidence: false,
  education: false,
  career: false,
  height: false,
  humor: false,
  intelligence: false,
  kindness: false
});

    const [animatingTraits, setAnimatingTraits] = useState({});

  const [highlightThreshold, setHighlightThreshold] = useState(70);

  const instructionsRef = useRef(null);
  const chartSectionRef = useRef(null);
  const conclusionRef = useRef(null);
const [instructionsHasScrolled, setInstructionsHasScrolled] = useState(false);
const [traitHasScrolled, setTraitHasScrolled] = useState(false);

const toggleTrait = (traitName) => {
  const newValue = !activeTraits[traitName];
  
  // Always update lastSelectedTrait when any trait is toggled
  setLastSelectedTrait(traitName);
  
  if (newValue) {
    setAnimatingTraits(prev => ({ ...prev, [traitName]: true }));
    
    setTimeout(() => {
      setActiveTraits(prev => ({ ...prev, [traitName]: true }));
      setAnimatingTraits(prev => ({ ...prev, [traitName]: false }));
    }, 50);
    
    // Scroll to chart section to see both chart and insight
    if (!traitHasScrolled) {
      setTimeout(() => {
        chartSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end'
        });
        setTraitHasScrolled(true);
      }, 150);
    }
  } else {
    setActiveTraits(prev => ({ ...prev, [traitName]: false }));
  }
};
  const createZeroData = (trait) => ({
    ...trait,
    data: trait.data.map(point => ({ ...point, y: 0 }))
  });

  const chartData = Object.entries(activeTraits)
    .filter(([traitName, isActive]) => isActive || animatingTraits[traitName])
    .map(([traitName, _]) => {
      const trait = traitDefinitions[traitName];
      return animatingTraits[traitName] ? createZeroData(trait) : trait;
    });

  const activeCount = Object.values(activeTraits).filter(Boolean).length;

  const handleSliderChange = (e) => {
    if (e.type === "touchstart" || e.type === "touchmove") {
      e.stopPropagation();
    }
    setHighlightThreshold(Number(e.target.value));
  };


  const hexToRgba = (hex, alpha = 0.12) => {
  const sanitized = hex.replace('#', '');
  const r = parseInt(sanitized.substring(0, 2), 16);
  const g = parseInt(sanitized.substring(2, 4), 16);
  const b = parseInt(sanitized.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

  return (
    <div style={{ 
      padding: '30px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>

            {/* Chart and trait buttons */}
      {/* <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '20px' }}>
        <div style={{ height: '360px', flex: 1, background: 'white', borderRadius: '12px', padding: '0px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', position: 'relative' }}> */}
      <h1 style={{ 
        fontSize: '32px', 
        fontWeight: '700', 
        // marginBottom: '10px',
        color: '#2d3748'
      }}>
        <em>
        Average is Ugly? 
        </em>

{/* Info Bar */}
<div style={{
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
  marginBottom: '20px',
  marginTop: '14px',
  marginLeft: '-6px'
}}>

  {/* Theme Pill */}
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f9fafb',
    border: '1px solid #e2e8f0',
    borderRadius: '9999px',
    padding: '5px 10px',
    fontSize: '13px',
    color: '#4a5568',
    height: '34px'
  }}>
    <strong style={{ color: '#8b8b8bff', fontWeight: 600 }}>Theme</strong>
    <div style={{ display: 'flex', gap: '4px', opacity: '.9' }}>
      {['🌹', '👁️', '🥀'].map((emoji, i) => (
        <span key={i} style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          border: '1px solid #cbd5e081',
          backgroundColor: '#edf2f7',
          fontSize: '13px'
        }}>
          {emoji}
        </span>
      ))}
    </div>
  </div>

  {/* Introspection Pill */}
  <div style={{ 
    display: 'flex', 
    alignItems: 'center',
    gap: '8px', 
    backgroundColor: '#f9fafb', 
    borderRadius: '9999px', 
    padding: '5px 10px', 
    border: '1px solid #e2e8f0',
    fontSize: '13px',
    color: '#4a5568',
    height: '34px'
  }}>
    <strong style={{ color: '#8b8b8bff', fontWeight: 600 }}>Introspection Needed</strong>
    <div style={{ display: 'flex', gap: '4px' }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          border: '1px solid #cbd5e0',
          backgroundColor: i <= 3 ? '#edf2f7' : 'transparent',
          fontSize: '13px',
          lineHeight: '1',
        }}>
          {i <= 3 ? '💭' : ''}
        </span>
      ))}
    </div>
  </div>

  {/* Read Time Pill */}
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f9fafb',
    border: '1px solid #e2e8f0',
    borderRadius: '9999px',
    padding: '5px 10px',
    fontSize: '13px',
    color: '#8b8b8bff',
    height: '34px'
  }}>
    {/* <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      backgroundColor: '#edf2f7',
      fontSize: '13px'
    }}>⏱</span> */}
    <span>5-7 min read</span>
  </div>

</div>

      </h1>
      <p className='plan-card-text'>
       The title might sound harsh at first, but by the end of this explainer, you may see how your preferences subtly reflects this idea that “average is ugly”.
      </p>
      <br/>

           {/* Question */}
<div  ref={instructionsRef} style={{
  padding: '16px',
  background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
  borderRadius: '20px',
  marginBottom: '40px'
}}>
  <Typography 
    variant="h6" 
    gutterBottom 
    style={{
      marginBottom: '16px',
      fontWeight: 600,
      color: '#2d3748',
      lineHeight: '26px'
    }}
  >
    Which of these complaints about dating do you relate to most?
  </Typography>


     {/* Persistent sentiment buttons */}
<div  style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
  {initialSentiments.map(sentiment => (
  <button
  key={sentiment.id}
  onClick={() => {setSelectedSentiment(sentiment.id) 
    if (!instructionsHasScrolled) setTimeout(() => {
      instructionsRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
    }, 400);
    setInstructionsHasScrolled(true)
  }}
  style={{
    flex: 1,
    padding: '12px 12px',
    borderRadius: '16px',
    border: selectedSentiment === sentiment.id ? 'none' : '2px solid #cbd5e0',
    background: selectedSentiment === sentiment.id
      ? (sentiment.id === 'lackOfMatches' ? '#ff5a7a' : 'linear-gradient(135deg, #3182ce, #63b3ed)')
      : 'white',
    color: selectedSentiment === sentiment.id ? 'white' : '#6d6d6dff',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 600,
    fontSize: '16px',
    boxShadow: selectedSentiment === sentiment.id
      ? '0 6px 15px rgba(49,130,206,0.2)'
      : '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  }}
  onMouseEnter={e => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
  }}
  onMouseLeave={e => {
    e.currentTarget.style.transform = 'translateY(0px)';
    e.currentTarget.style.boxShadow = selectedSentiment === sentiment.id 
      ? '0 6px 15px rgba(49,130,206,0.2)' 
      : '0 1px 3px rgba(0,0,0,0.1)';
  }}
>
      <span>{sentiment.title}</span>
      <span style={{
        fontWeight: 400,
        fontSize: '12px',
        marginTop: '8px',
        color: selectedSentiment === sentiment.id ? '#d0ebff' : '#878787ff',
        lineHeight: '16px'
      }}>
        {sentiment.subtitle}
      </span>
    </button>
  ))}
</div>
</div>



      {/* Threshold Slider
      <div style={{ 
        marginBottom: '30px', 
        padding: '20px', 
        background: '#f7fafc', 
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '12px', 
          fontSize: '14px', 
          fontWeight: '600',
          color: '#8b8b8bff'
        }}>
          Your Minimum Requirement: {highlightThreshold}th percentile
        </label>
        <input
          type="range"
          min="50"
          max="99"
          value={highlightThreshold}
          onChange={handleSliderChange}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          style={{ 
            width: '100%', 
            height: '6px',
            cursor: 'pointer',
            WebkitAppearance: 'none'
          }}
        />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          fontSize: '12px', 
          color: '#718096',
          marginTop: '8px'
        }}>
          <span>Average</span>
          <span>Top 1%</span>
        </div>
      </div> */}
      {selectedSentiment && (

      <Fade in={!!selectedSentiment} timeout={800}>
        <div>
{/* Compact Instructions */}
        <div
         style={{
          padding: '10px 16px',
          background: '#f7fafc',
          borderRadius: '14px',
          border: '2px solid #e6e6e6ff',
          marginBottom: '10px',
        }}>
          <p style={{
            fontSize: '14px',
            color: '#696969ff',
            lineHeight: 1.4,
            margin: 0,
          }}>
            <strong>💡Instructions: </strong>
            {selectedSentiment === 'lackOfMatches' 
              ? 'Select the traits your partner needs to be above average in.'
              : 'Select the traits you think you’re expected to be above average in to find a partner.'}
          </p>
        </div>


      {/* Insight Section */}
<Fade in={!!(lastSelectedTrait && selectedSentiment)} timeout={400} unmountOnExit>
<div style={{ 
  marginBottom: '10px', 
  padding: '14px 18px', 
  borderRadius: '12px',
  fontSize: '14px',
  color: '#4a5568',
  lineHeight: '1.6',
  border: `2px solid ${traitDefinitions[lastSelectedTrait]?.color || '#e2e8f0'}`,
  background: lastSelectedTrait ? hexToRgba(traitDefinitions[lastSelectedTrait].color, 0.12) : 'transparent',
  boxShadow: `0 0 10px ${traitDefinitions[lastSelectedTrait]?.color || '#e2e8f0'}40`,
  transition: 'all 1s ease',
}}>
  {lastSelectedTrait && selectedSentiment && (
    <>
      <div style={{ 
        fontSize: '11px', 
        color: traitDefinitions[lastSelectedTrait].color,
        marginBottom: '6px',
        fontWeight: 600,
        opacity: 0.8
      }}>
        💭 INTROSPECTION AID
      </div>
      <div>
        <strong>{traitDefinitions[lastSelectedTrait].id}:</strong> {selectedSentiment === 'lackOfMatches' ? traitDefinitions[lastSelectedTrait].insight : traitDefinitions[lastSelectedTrait].altInsight}
      </div>
    </>
  )}
</div>
</Fade>

      {/* Chart and Buttons Side by Side */}
       <div ref={chartSectionRef}>
      <div style={{ 
        display: 'flex', 
        gap: '10px',
        alignItems: 'flex-start',
        // marginLeft: '-20px',
        // marginRight: '-20px',
      }}>
        {/* Chart */}
        <div style={{ 
          height: '370px',
          flex: 1,
          background: 'white', 
          borderRadius: '12px', 
          padding: '0px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}>
          <ResponsiveLine
            data={chartData}
            margin={{ top: 20, right: 10, bottom: 30, left: 20 }}
            xScale={{ type: 'linear', min: 0, max: 100 }}
            yScale={{ type: 'linear', min: 0, max: 3.2 }}
            curve="basis"
            axisBottom={{
              legend: '',
              legendOffset: 45,
              legendPosition: 'middle',
              tickSize: 0,
              tickValues: [15, 50, 85],
              format: v => v === 15 ? 'Below Avg' : v === 50 ? 'Avg' : 'Above Avg'
            }}
            axisLeft={null}
            colors={d => d.color}
            lineWidth={2}
            enablePoints={false}
            enableArea={false}
            useMesh={false}
            animate={true}
            motionConfig="gentle"
            enableSlices={false}
            legends={[]}
            markers={[
              {
                axis: 'x',
                value: highlightThreshold,
                lineStyle: { 
                  stroke: '#6fa286b3', 
                  strokeWidth: 2,
                  strokeDasharray: '6 4'
                }
              }
            ]}
            theme={{
              fontSize: 12,
              axis: {
                legend: {
                  text: {
                    fontSize: 13,
                    fontWeight: 600
                  }
                },
                ticks: {
                  text: {
                    fontSize: 11,
                    fill: '#718096'
                  }
                }
              }
            }}
            layers={[
              'grid',
              'markers',
              'axes',
              ({ xScale, yScale, innerHeight }) => {
                const x = xScale(highlightThreshold);
                const width = xScale(100) - x;
                return (
                  <rect
                    x={x}
                    y={0}
                    width={width}
                    height={innerHeight}
                    fill="rgba(62, 229, 112, 0.08)"
                  />
                );
              },
              'areas',
              'lines',
              'slices',
              'points',
              'mesh',
              ({ yScale }) => {
                const yMin = yScale.range()[0];
                const yMax = yScale.range()[1];
                const yMid = (yMin + yMax) / 2;
                return (
                  <g>
                    <text
                      x={-15}
                      y={yMin + 20}
                      transform={`rotate(-90, -45, ${yMin - 20})`}
                      textAnchor="middle"
                      style={{ fontSize: 11, fill: '#718096' }}
                    >
                      Less People
                    </text>
                    <text
                      x={85}
                      y={yMid + 60}
                      transform={`rotate(-90, -45, ${yMid + 20})`}
                      textAnchor="middle"
                      style={{ fontSize: 11, fill: '#718096' }}
                    >
                      More People
                    </text>
                  </g>
                );
              }
            ]}
          />
        </div>

        {/* Trait Toggle Buttons */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '8px',
        }}>
          {Object.entries(traitDefinitions).map(([key, trait]) => (
            <button
              key={key}
              onClick={() => toggleTrait(key)}
              style={{
                padding: '6px 4px',
                border: `2px solid ${trait.color}`,
                borderRadius: '6px',
                background: activeTraits[key] ? trait.color : 'white',
                color: activeTraits[key] ? 'white' : trait.color,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: activeTraits[key] ? '0 2px 6px rgba(0,0,0,0.12)' : 'none',
                textAlign: 'left'
              }}
            >
              {trait.id}
            </button>
          ))}
        </div>
      </div>

    </div>

            {/* Finished Button */}

      {traitHasScrolled && (
        <>

        <div style={{
  display: 'flex',
  justifyContent: 'center',
  marginTop: '10px',
  marginBottom: '20px'
}}>
  <button
    onClick={() => {
      conclusionRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
    }}
    style={{
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      border: 'none',
      background: '#cae4ff68',
      color: '#0084ff80',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      animation: 'gentleFloat 2.5s ease-in-out infinite',
      fontSize: '20px'
    }}

  >
    <ArrowDownwardIcon />
  </button>
  

</div>

{/* Conclusion Section */}
<div ref={conclusionRef} style={{
  marginTop: '30px',
  padding: '10px',
  background: 'white',
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  textAlign: 'center',  // centers inline content
  maxWidth: '400px',    // constrain overall width
  marginLeft: 'auto',
  marginRight: 'auto'
  
}}>
  {(() => {
    const selectedCount = Object.values(activeTraits).filter(Boolean).length;
    const maxTraits = 10;

    // Emoji scale (happy → sad → angry)
const emojiScale = ['🙂','😐','😬', '🙁','😮‍💨','😢','😰','😭','😤','😵'];

    return (
      <>
        {/* Big counter */}
        <div style={{
          fontSize: '35px',
          fontWeight: '700',
          color: '#2d3748',
          marginBottom: '5px'
        }}>
          {selectedCount}/{maxTraits}
        </div>

             {/* Smaller title */}
        <h3 style={{
          fontSize: '18px',
          fontWeight: 500,
          color: '#4a5568',
          marginTop: '0px',
          marginBottom: '10px'
        }}>
          {selectedSentiment === 'lackOfMatches' 
              ? 'traits that partner needs to be above average in'
              : 'traits expected to be above average in'}
        </h3>


                {/* Emoji meter */}
  <div style={{
    display: 'inline-flex',   // shrinks to fit content
    gap: '1px'
  }}>
    {emojiScale.map((emoji, index) => (
      <span key={index} style={{
        opacity: index < selectedCount ? .85 : 0.3,
        fontSize: '20px',
        transition: 'opacity 0.3s ease'
      }}>
        {emoji}
      </span>
    ))}
        </div>

   

      </>
    );
  })()}


</div>
      <br/>
      <br/>

<p className='plan-card-text'>
  The chart above isn’t meant to be a literal statistic, instead, it’s more of a reflection aid. It’s natural to expect great things from a partner, but watching each line appear as you select traits shows just how rare it is for someone to meet those expectations.
</p>
<br/>

<p className='plan-card-quote'>
  Being above average in even one of those traits is already rare if you think about it.
</p>
<br/>

<p className='plan-card-text'>
  Now imagine finding someone who’s kind, intelligent, and attractive — all above average. If we (simplistically) assume these 3 traits are independent and you’re looking at the top 25% for each, your dating pool drops to  ~1.5% of people (0.25 × 0.25 × 0.25).
</p>
<br/>


</>
      )}
        </div>

        </Fade>
      )}

     </div>
    // </div>
  );
}




// {selectedSentiment && (
//   <div style={{
//     display: 'flex',
//     justifyContent: 'center',
//     marginTop: '30px',
//     marginBottom: '20px'
//   }}>
//     <button
//   onClick={() => {
//     conclusionRef.current?.scrollIntoView({ 
//       behavior: 'smooth', 
//       block: 'start'
//     });
//   }}
//   style={{
//     padding: '12px 28px',
//     borderRadius: '50px',
//     border: 'none',
//     background: 'linear-gradient(135deg, #3182ce, #63b3ed)',
//     color: 'white',
//     cursor: 'pointer',
//     fontSize: '15px',
//     fontWeight: 600,
//     boxShadow: '0 4px 12px rgba(49,130,206,0.3)',
//     transition: 'all 0.2s ease',
//     display: 'flex',
//     alignItems: 'center',
//     gap: '8px'
//   }}
//   onMouseEnter={e => {
//     e.currentTarget.style.transform = 'translateY(-2px)';
//     e.currentTarget.style.boxShadow = '0 6px 16px rgba(49,130,206,0.4)';
//   }}
//   onMouseLeave={e => {
//     e.currentTarget.style.transform = 'translateY(0px)';
//     e.currentTarget.style.boxShadow = '0 4px 12px rgba(49,130,206,0.3)';
//   }}
// >
//     <span>Finished</span>
//   <span>→</span>
// </button>
//   </div>
// )}